import type { PortalDsBadgeVariant } from 'src/components/portal-design-system';
import type {
  PortalMetrics,
  PortalMetricsViewModel,
  PortalSubscriptionUsage,
  PortalUser,
  PortalUserSubscriptionTier,
} from '../types/users.types';

export function mapPortalUser(user: PortalUser): PortalUser {
  return user;
}

export function computeNewUsersDelta(
  current: number,
  prior: number
): Pick<PortalMetricsViewModel, 'newUsersDelta' | 'newUsersDeltaDirection'> {
  if (prior === 0) {
    return {
      newUsersDelta: '— vs prior 30d',
      newUsersDeltaDirection: 'neutral',
    };
  }

  const percentChange = ((current - prior) / prior) * 100;
  const rounded = Math.round(percentChange);
  const sign = rounded > 0 ? '+' : '';
  const direction = rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'neutral';

  return {
    newUsersDelta: `${sign}${rounded}% vs prior 30d`,
    newUsersDeltaDirection: direction,
  };
}

export function mapPortalMetrics(metrics: PortalMetrics): PortalMetricsViewModel {
  const delta = computeNewUsersDelta(metrics.new_users_30d, metrics.new_users_prior_30d);

  return {
    newUsersLast30d: metrics.new_users_30d,
    creativesGeneratedLast30d: metrics.creatives_generated_30d,
    brandsIngestedLast30d: metrics.brands_ingested_30d,
    ...delta,
  };
}

export function formatSubscriptionLabel(subscription: PortalUserSubscriptionTier): string {
  return subscription
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function getSubscriptionBadgeVariant(tier: PortalUserSubscriptionTier): PortalDsBadgeVariant {
  switch (tier) {
    case 'basic':
    case 'pro':
      return 'blue';
    case 'media_partner':
      return 'amber';
    case 'internal':
      return 'gray';
    case 'free':
    default:
      return 'gray';
  }
}

export function resolvePortalUserUsage(user: PortalUser): PortalSubscriptionUsage {
  if (user.usage) {
    return user.usage;
  }

  return {
    gen_edit_used: user.monthly_used,
    gen_edit_limit: user.monthly_limit,
    resize_used: 0,
    resize_limit: user.monthly_limit,
  };
}

export function isPaidSubscriptionTier(tier: PortalUserSubscriptionTier): boolean {
  return tier === 'basic' || tier === 'pro';
}

export function formatStatusLabel(status: PortalUser['status']): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}
